(ns renolit-roofing-service.view.installer-service-detail
  (:require [malli.util :as mu]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.shared.domain.types :as dom.types]
            [renolit-roofing-service.tooltip :as tooltip]
            [renolit-roofing-service.tooltip.generic-popup :as tooltip.generic-popup]
            [renolit-roofing-service.view :as view]
            [renolit-roofing-service.view.installer-add-review-popup :as add-review-popup]
            [renolit-roofing-service.util.google-maps :as google-maps]
            [renolit-roofing-service.view.installer-service-information.zone :as view.zone]))

(def path-params-schema
  [:map
   [:id
    [:uuid]]])

(def ^:const route-config
  {:name [:installer-service-detail]
   :auth-req? true
   :parameters {:path path-params-schema}
   :controllers [{:parameters {:path (mu/keys path-params-schema)}
                  :start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 (fn [_ [_ opts]]
   (let [service-id (get-in opts [:path :id])]
     {:fx [[:dispatch [::srv.service/fetch-service-by-id service-id]]
           [:dispatch [::set-active-tab :log]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [_ _]
   {:fx [[:dispatch [::srv.service/clear-service]]]}))

(rf/reg-event-fx
 ::set-active-tab
 (fn [{:keys [db]} [_ tab]]
   (cond-> {:db (-> db
                    (assoc :installer-service-detail-active-tab tab)
                    (assoc :resumen-view-mode :map))}
     (= tab :resumen) (assoc :fx [[:dispatch [::initialize-zones-map-filtered]]]))))

(rf/reg-event-fx
 ::toggle-resumen-view-mode
 (fn [{:keys [db]} _]
   (let [current (get db :resumen-view-mode :map)]
     (if (= current :map)
       {:db (assoc db :resumen-view-mode :list)}
       {:db (assoc db :resumen-view-mode :map)
        :fx [[:dispatch [::initialize-zones-map-filtered]]]}))))

(rf/reg-sub
 ::resumen-view-mode
 (fn [db _]
   (get db :resumen-view-mode :map)))

(rf/reg-sub
 ::active-tab
 (fn [db _]
   (get db :installer-service-detail-active-tab :log)))

(rf/reg-sub
 ::project
 :<- [::srv.service/current-service]
 (fn [service _]
   (:project service)))

(rf/reg-sub
 ::derived-work-packages
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [clear-obs-by-wp (->> (or (:observations service) [])
                              (filter :clear)
                              (group-by :wp))]
     (->> (or (:reviews service) [])
          (group-by :wp)
          (mapv (fn [[wp wp-reviews]]
                  {:wp wp
                   :total-tasks (reduce (fn [acc review]
                                          (+ acc (count (:tasks review))))
                                        0
                                        wp-reviews)
                   :completed-tasks (count (get clear-obs-by-wp wp []))}))))))

;; ---- Filter panel state ----

(rf/reg-event-db
 ::toggle-filter-panel
 (fn [db _]
   (update db :filter-panel-open? not)))

(rf/reg-event-fx
 ::set-applied-filters
 (fn [{:keys [db]} [_ filters]]
   (let [new-db (assoc db :applied-filters filters)
         on-resumen? (= :resumen (get db :installer-service-detail-active-tab))]
     (cond-> {:db new-db}
       on-resumen? (assoc :fx [[:dispatch [::initialize-zones-map-filtered]]])))))

(rf/reg-sub
 ::filter-panel-open?
 (fn [db _]
   (get db :filter-panel-open? false)))

(rf/reg-sub
 ::applied-filters
 (fn [db _]
   (get db :applied-filters nil)))

(rf/reg-sub
 ::filters-active?
 :<- [::applied-filters]
 (fn [filters _]
   (some? filters)))

;; ---- Observations (with filter applied) ----

(rf/reg-sub
 ::observations-for-list
 :<- [::srv.service/current-service]
 :<- [::applied-filters]
 (fn [[service filters] _]
   (let [zones (or (:zones service) [])
         zone-index (into {} (map (juxt :id :name)) zones)
         reviews (or (:reviews service) [])
         review-index (into {} (map (juxt :id identity)) reviews)
         observations (or (:observations service) [])]
     (->> observations
          (mapv (fn [obs]
                  (let [review (get review-index (:review-id obs))
                        zone-id (:zone-id review)
                        zone-name (get zone-index zone-id "")
                        zone (->> zones (filter #(= (:id %) zone-id)) first)
                        subzone-names (->> (:subzones zone)
                                           (keep (fn [sz-id] (get zone-index sz-id))))]
                    (assoc obs
                           :zone-id zone-id
                           :zone-name zone-name
                           :subzone-names subzone-names
                           :review-type (:type review)
                           :wp-type (:wp-type review)))))
          (remove :clear)
          (filter (fn [{:keys [zone-id task-type]}]
                    (or (nil? filters)
                        (let [{:keys [zones task-types]} filters]
                          (and (or (nil? zones) (contains? zones (str zone-id)))
                               (or (nil? task-types) (contains? task-types (keyword task-type))))))))))))

(rf/reg-sub
 ::all-work-packages-completed?
 :<- [::derived-work-packages]
 (fn [work-packages _]
   (and (seq work-packages)
        (every? (fn [{:keys [total-tasks completed-tasks]}]
                  (and (pos? total-tasks) (= total-tasks completed-tasks)))
                work-packages))))

(rf/reg-sub
 ::service-submitted?
 :<- [::srv.service/current-service]
 (fn [service _]
   (= :laboratory (keyword (:status service)))))

;; ---- Filtered service (for map + zone panel) ----

(defn- filter-service
  [service filters]
  (if (nil? filters)
    service
    (let [{:keys [zones task-types]} filters
          filtered-reviews
          (->> (or (:reviews service) [])
               (keep (fn [{:keys [tasks] :as review}]
                       (let [matched-tasks (if (nil? task-types)
                                             tasks
                                             (filterv #(contains? task-types (keyword (:task-type %))) tasks))]
                         (when (or (nil? task-types) (seq matched-tasks))
                           (assoc review :tasks matched-tasks)))))
               vec)
          filtered-zones (if (nil? zones)
                           (:zones service)
                           (filterv #(contains? zones (str (:id %))) (:zones service)))]
      (assoc service :zones filtered-zones :reviews filtered-reviews))))

(rf/reg-event-fx
 ::initialize-zones-map-filtered
 (fn [{:keys [db]} _]
   (let [service  (get db :current-service)
         filtered (filter-service service (get db :applied-filters))
         zones    (or (:zones filtered) [])
         reviews  (or (:reviews filtered) [])
         obs      (or (:observations service) [])
         gmaps-data (view.zone/api-zones->gmaps-data zones reviews obs)
         viewport   (view.zone/compute-viewport zones)]
     (when (and (seq zones) viewport)
       {:db (-> db
                (assoc :default-map-viewport viewport)
                (assoc :gmaps/read-only? true))
        :fx [[:dispatch [::google-maps/initialize-zones gmaps-data]]]}))))

(rf/reg-sub
 ::resumen-filtered-service
 :<- [::srv.service/current-service]
 :<- [::applied-filters]
 (fn [[service filters] _]
   (filter-service service filters)))

;; ---- Filter panel ----

(defn- all-task-types-for-wp [wp]
  (into #{} cat (vals (get dom.types/wp-hierarchy wp))))

(defn- all-task-types []
  (into #{} (comp (mapcat vals) cat) (vals dom.types/wp-hierarchy)))

(defn- zone-subtree-ids
  "All IDs in a zone's subtree (as strings): the zone itself plus all subzone IDs."
  [zone all-zones]
  (let [subzone-ids (into #{} (map str (:subzones zone)))
        subzones    (filter #(contains? subzone-ids (str (:id %))) all-zones)]
    (into #{(str (:id zone))} (mapcat #(zone-subtree-ids % all-zones) subzones))))

(defn- filter-checkbox
  [{:keys [checked? indeterminate? on-change label extra-class icon?]}]
  [:label {:class (str "resumen-filter-item" (when extra-class (str " " extra-class)))}
   [:input.resumen-filter-item__input
    {:type "checkbox"
     :checked (boolean checked?)
     :ref #(when % (set! (.-indeterminate %) (boolean indeterminate?)))
     :on-change (fn [e] (on-change (.. e -target -checked)))}]
   [:span.resumen-filter-item__label label]
   (when icon?
     [layout.icon/icon {:key :info}])])

(defn- wp-type-section
  [pending wp wp-type tasks]
  (let [checked? (every? #(contains? (:task-types @pending) %) tasks)
        indet?   (and (not checked?) (some #(contains? (:task-types @pending) %) tasks))
        toggle   (fn [on?]
                   (swap! pending update :task-types
                          (if on? into #(reduce disj % tasks)) tasks))]
    [:<>
     [filter-checkbox {:checked? checked? :indeterminate? indet?
                       :extra-class "resumen-filter-item--indent-1"
                       :label (t [(keyword (str "domain.choices.wp-type/" (name wp-type)))])
                       :on-change toggle}]
     (for [task-type (sort tasks)]
       ^{:key (str (name wp) "-" (name wp-type) "-" (name task-type))}
       [filter-checkbox
        {:checked?    (contains? (:task-types @pending) task-type)
         :extra-class "resumen-filter-item--indent-2"
         :icon?       true
         :label       (t [(keyword (str "domain.choices.task-type/" (name task-type)))])
         :on-change   (fn [on?]
                        (swap! pending update :task-types
                               (if on? conj disj) task-type))}])]))

(defn- wp-section
  [pending wp wp-types]
  (let [wp-tasks (all-task-types-for-wp wp)
        checked? (every? #(contains? (:task-types @pending) %) wp-tasks)
        indet?   (and (not checked?) (some #(contains? (:task-types @pending) %) wp-tasks))
        toggle   (fn [on?]
                   (swap! pending update :task-types
                          (if on? into #(reduce disj % wp-tasks)) wp-tasks))]
    [:<>
     [filter-checkbox {:checked? checked? :indeterminate? indet?
                       :label (t [(keyword (str "domain.choices.wp/" (name wp)))])
                       :on-change toggle}]
     (for [[wp-type tasks] (sort-by (comp name first) wp-types)]
       ^{:key (str (name wp) "-" (name wp-type))}
       (if (= wp-type :nil)
         (for [task-type (sort tasks)]
           ^{:key (str (name wp) "-nil-" (name task-type))}
           [:div.resumen-filter-item.resumen-filter-item--indent-1
            [filter-checkbox
             {:checked? (contains? (:task-types @pending) task-type)
              :extra-class "resumen-filter-item--indent-1"
              :label (t [(keyword (str "domain.choices.task-type/" (name task-type)))])
              :on-change (fn [on?]
                           (swap! pending update :task-types
                                  (if on? conj disj) task-type))}]
            [layout.icon/icon {:key :info}]])
         [wp-type-section pending wp wp-type tasks]))]))

(defn- zone-row
  [pending zone all-zones indent?]
  (let [ids     (zone-subtree-ids zone all-zones)
        checked? (every? #(contains? (:zones @pending) %) ids)
        indet?   (and (not checked?) (some #(contains? (:zones @pending) %) ids))
        toggle   (fn [on?]
                   (swap! pending update :zones
                          (if on? into #(reduce disj % ids)) ids))]
    [:<>
     [filter-checkbox {:checked? checked? :indeterminate? indet?
                       :extra-class (when indent? "resumen-filter-item--indent-1")
                       :label (:name zone)
                       :on-change toggle}]
     (let [subzone-id-set (into #{} (:subzones zone))
           subzones (filter #(contains? subzone-id-set (:id %)) all-zones)]
       (for [sz subzones]
         ^{:key (:id sz)}
         [zone-row pending sz all-zones true]))]))

(defn- filter-panel
  []
  (let [service-sub         (rf/subscribe [::srv.service/current-service])
        applied-filters-sub (rf/subscribe [::applied-filters])
        all-zones           (or (:zones @service-sub) [])
        all-subzone-ids     (into #{} (map str (mapcat :subzones all-zones)))
        root-zones          (remove #(contains? all-subzone-ids (str (:id %))) all-zones)
        ;; Always use strings for zone IDs so UUID vs string type doesn't matter
        all-zone-ids        (into #{} (map #(str (:id %)) all-zones))
        all-tt              (all-task-types)
        init-filters        @applied-filters-sub
        all-selected        {:zones all-zone-ids :task-types all-tt}
        pending             (r/atom {:zones      (or (:zones init-filters) all-zone-ids)
                                     :task-types (or (:task-types init-filters) all-tt)})]
    (fn []
      (let [clear-all     #(reset! pending all-selected)
            apply-filters
            (fn []
              (let [{:keys [zones task-types]} @pending
                    f (cond-> {}
                        (not= zones all-zone-ids) (assoc :zones zones)
                        (not= task-types all-tt)  (assoc :task-types task-types))]
                (rf/dispatch [::set-applied-filters (when (seq f) f)])
                (rf/dispatch [::toggle-filter-panel])))]
        [:div.resumen-filter-panel
         [:div.resumen-filter-panel__header
          [:button.resumen-filter-panel__back-btn
           {:on-click #(rf/dispatch [::toggle-filter-panel])}
           [layout.icon/icon {:key :back}]]
          [:h3.resumen-filter-panel__title "Filtros"]
          [:button.resumen-filter-panel__clear-btn
           {:on-click clear-all}
           "Eliminar todo"]]

         [:div.resumen-filter-panel__content
          ;; Zones
          [:div.resumen-filter-panel__section
           [:h4.resumen-filter-panel__section-title "Añadir"]
           (for [zone root-zones]
             ^{:key (:id zone)}
             [zone-row pending zone all-zones false])]

          ;; Visit program
          [:div.resumen-filter-panel__section
           [:h4.resumen-filter-panel__section-title "Programa de visita"]
           (for [[wp wp-types] (sort-by (comp name first) dom.types/wp-hierarchy)]
             ^{:key (name wp)}
             [wp-section pending wp wp-types])]]

         [:div.resumen-filter-panel__footer
          [:button.btn.btn--primary.btn--full-width
           {:on-click apply-filters}
           "Aplicar"]]]))))

;; ---- Confirm submit popup ----

(def ^:const confirm-popup-id tooltip.generic-popup/default-popup-id)

(defn- confirm-submit-popup
  [{:keys [service-id]}]
  [tooltip.generic-popup/generic-confirmation-popup
   {:title-transl-kw :page.installer.service-detail/confirm-submit-title
    :line-1-transl-kw :page.installer.service-detail/confirm-submit-message
    :line-2-transl-kw :page.installer.service-detail/confirm-submit-warning
    :on-cancel {}
    :on-accept {:fn (fn []
                      (rf/dispatch [::submit-to-lab service-id])
                      (rf/dispatch [::tooltip/destroy-by-id {:id confirm-popup-id}]))}}])

(rf/reg-event-fx
 ::open-confirm-submit
 (fn [_ [_ {:keys [service-id]}]]
   {:fx [[:dispatch [::tooltip/register {:id confirm-popup-id
                                         :component confirm-submit-popup
                                         :argv [{:service-id service-id}]
                                         :class ["generic-popup-standard"]}]]]}))

(rf/reg-event-fx
 ::submit-to-lab
 (fn [_ [_ service-id]]
   {:fx [[:dispatch [::srv.service/installer-submit
                     service-id
                     {:on-success-evt [::submit-to-lab-success]}]]]}))

(rf/reg-event-fx
 ::submit-to-lab-success
 (fn [_ _]
   {:fx [[:dispatch [::nav/push-state [:installer-home]]]]}))

(defn- work-package-card
  [{:keys [wp total-tasks completed-tasks]} service-id]
  (let [percentage (if (pos? total-tasks)
                     (* 100 (/ completed-tasks total-tasks))
                     0)
        wp-label (t [(keyword (str "domain.choices.wp/" (name wp)))])
        href (nav/href [:installer-review-wp-detail] {:id service-id :wp (name wp)})]
    [:a.work-package-card
     {:href href}
     [:div.work-package-card__header
      [:div.work-package-card__icon
       [layout.icon/icon {:key :triangle}]]
      [:span.work-package-card__title wp-label]
      [:div.work-package-card__info
       [layout.icon/icon {:key :info}]]
      [:div.work-package-card__chevron
       [layout.icon/icon {:key :right-arrow}]]]
     [:div.work-package-card__progress
      [:div.work-package-card__progress-bar
       [:div.work-package-card__progress-bar-fill
        {:style {:width (str percentage "%")}}]]
      [:span.work-package-card__progress-text
       (t [:page.installer.service-detail.tasks-progress]
          [completed-tasks total-tasks])]]]))

(defn- tabs
  []
  (let [active-tab (rf/subscribe [::active-tab])]
    (fn []
      [:div.service-detail-tabs.tab-bar.tab-bar--pill
       [:div.service-detail-tab.tab-bar__tab
        {:class (when (= :log @active-tab) "tab-bar__tab--selected")
         :on-click #(rf/dispatch [::set-active-tab :log])}
        "Log"]
       [:div.service-detail-tab.tab-bar__tab
        {:class (when (= :resumen @active-tab) "tab-bar__tab--selected")
         :on-click #(rf/dispatch [::set-active-tab :resumen])}
        "Resumen del servicio"]])))

(defn- work-packages-list
  [work-packages service-id]
  [:div.work-packages-list
   (if (seq work-packages)
     (for [wp work-packages]
       ^{:key (:wp wp)}
       [work-package-card wp service-id])
     [:div.empty-state
      [:p (t [:page.installer.service-detail.no-work-packages])]])])

(defn- format-time
  [date]
  (when date
    (let [d (if (inst? date) date (js/Date. date))
          h (.getHours d)
          m (.getMinutes d)]
      (str (when (< h 10) "0") h ":" (when (< m 10) "0") m "h"))))

(defn- observation-card
  [{:keys [id ui-id wp wp-type zone-name subzone-names task-type created-at]}]
  (let [wp-type-label (when (and wp-type (not= wp-type :nil))
                        (t [(keyword (str "domain.choices.wp-type/" (name wp-type)))]))
        wp-label (t [(keyword (str "domain.choices.wp/" (name wp)))])
        task-type-label (t [(keyword (str "domain.choices.task-type/" (name task-type)))])
        zone-display (if (seq subzone-names)
                       (str zone-name " / " (first subzone-names))
                       zone-name)
        id-short (or ui-id (str "I" (subs (str id) 0 8)))]
    [:div.review-card
     [:div.review-card__header
      [:div.review-card__title-row
       [layout.icon/icon {:key :info}]
       [:span.review-card__id id-short]]
      (when created-at
        [:span.review-card__time (format-time created-at)])]
     [:div.review-card__body
      [:p.review-card__wp-type (or wp-type-label wp-label)]
      (when (seq zone-display)
        [:p.review-card__zone zone-display])]
     [:div.review-card__tags
      [:span.review-card__tag task-type-label]
      (when wp-type-label
        [:span.review-card__tag wp-type-label])]]))

(defn- observations-list
  [observations]
  [:div.reviews-list
   (if (seq observations)
     (for [obs observations]
       ^{:key (:id obs)}
       [observation-card obs])
     [:div.empty-state
      [:p (t [:common/no-data])]])])

(defn- resumen-toolbar
  []
  (let [view-mode      (rf/subscribe [::resumen-view-mode])
        filters-active? (rf/subscribe [::filters-active?])]
    (fn []
      [:div.si-map-toolbar
       [:button.si-map-toolbar__item
        {:class    (when @filters-active? "si-map-toolbar__item--active")
         :on-click #(rf/dispatch [::toggle-filter-panel])}
        [layout.icon/icon {:key :filter}]
        [:span "Filtros"]
        (when @filters-active?
          [:span.si-map-toolbar__active-dot])]
       [:button.si-map-toolbar__item
        {:on-click #(rf/dispatch [::toggle-resumen-view-mode])}
        [layout.icon/icon {:key (if (= @view-mode :map) :list :map)}]
        [:span (if (= @view-mode :map) "Ver lista" "Ver mapa")]]])))

(defn- main-content
  []
  (let [active-tab       (rf/subscribe [::active-tab])
        service          (rf/subscribe [::srv.service/current-service])
        filtered-service (rf/subscribe [::resumen-filtered-service])
        work-packages    (rf/subscribe [::derived-work-packages])
        view-mode        (rf/subscribe [::resumen-view-mode])
        observations     (rf/subscribe [::observations-for-list])]
    (fn []
      (let [service-id (:id @service)]
        [:div.service-detail__content
         (case @active-tab
           :log [work-packages-list @work-packages service-id]
           :resumen [:<>
                     [resumen-toolbar]
                     (if (= @view-mode :map)
                       [view.zone/zones-tab filtered-service]
                       [observations-list @observations])]
           [work-packages-list @work-packages service-id])]))))

(defn- fab-button
  [service-id]
  [:button.fab-button
   {:on-click #(rf/dispatch [::add-review-popup/open {:service-id service-id}])}
   [:span.fab-button__icon "+"]])

(defn- bottom-action-button
  [service-id]
  (let [all-completed? (rf/subscribe [::all-work-packages-completed?])]
    (fn [service-id]
      [:div.bottom-action
       [:button.btn.btn--primary.btn--full-width
        {:disabled (not @all-completed?)
         :on-click #(rf/dispatch [::open-confirm-submit {:service-id service-id}])}
        (t [:common/finalize-and-send])
        [layout.icon/icon {:key :right-arrow}]]])))

(defn- main
  []
  (let [service (rf/subscribe [::srv.service/current-service])
        project (rf/subscribe [::project])
        submitted? (rf/subscribe [::service-submitted?])
        filter-open?    (rf/subscribe [::filter-panel-open?])]
    (fn []
      (let [title (if @project
                    (str (:number @project) " / " (or (:building-city @project)
                                                      (:building-state @project)
                                                      (:building-country @project)
                                                      ""))
                    (t [:page.installer.service-detail.title]))]
        [:div.user_layout
         [:div.user_layout_header
          [:a.btn.btn--transparent.btn--anchor.back-link
           {:href (nav/href [:installer-home])}
           [layout.icon/icon {:key :back}]]
          [:h3 title]
          [:a.btn.btn--transparent.btn--anchor {:href (nav/href [:installer-service-information] {:id (:id @service)})}
           [layout.icon/icon {:key :info}]]]
         (if @filter-open?
           [filter-panel]
           [:div.installer-service-detail
            [tabs]
            (if @service
              [:<>
               [main-content]
               (when-not @submitted?
                 [:<>
                  [fab-button (:id @service)]
                  [bottom-action-button (:id @service)]])]
              [:div.loading (t [:common.loading])])])]))))

(defmethod view/view-display (:name route-config)
  [_]
  [main])
